// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/Workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface PostAddUserToAWorkspaceProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  userId: string;
  isOwner: boolean;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @param workspaceId Workspace ID
 * @param userId User ID
 * @param isOwner Whether the user is an owner
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Adds a user to a workspace
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace ID
 * @param userId User ID
 * @param isOwner Whether the user is an owner
 * @returns Promise with API response or error
 */
export const postAddUserToAWorkspace = async ({
  getAccessToken,
  workspaceId,
  userId,
  isOwner,
}: PostAddUserToAWorkspaceProps): Promise<any> => {
  try {
    console.log('🔄 Adding user to workspace...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/user/${userId}/isOwner/${isOwner}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ User added to workspace successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error adding user to workspace:', error);
    throw error;
  }
};
